/**
 * The ink engine. This is the part that has to feel like paper.
 *
 * Design notes:
 *  - Pointer Events only. `pointerType` tells pen from finger, `pressure` drives
 *    stroke width, and `getCoalescedEvents()` recovers the sub-frame samples Safari
 *    batches up so fast strokes stay smooth instead of turning into polygons.
 *  - Palm rejection: once a pen has been seen, touch input is ignored for a grace
 *    period. Resting a hand on the glass while writing does nothing.
 *  - The live stroke is drawn incrementally on every pointermove, so ink appears
 *    within one frame. A full repaint only happens on undo/erase/resize.
 *  - Stroke geometry is stored in CSS pixels relative to the canvas; the canvas
 *    itself is scaled by devicePixelRatio so lines stay crisp on Retina iPads.
 */

import type { InkPoint, InkTool, Stroke } from './types';
import { strokeBounds } from './types';
import type { StaffLayout } from '../theory/staff';
import {
  deleteSelection,
  duplicateSelection,
  polygonArea,
  selectionBounds,
  snapDeltaY,
  strokesInLasso,
  transformSelection,
  type Point2D,
} from './selection';

/**
 * A page of writing, detached from any canvas.
 *
 * See `InkEngine.serialize`. This is the unit `InkSession` keeps alive while the
 * pad is being re-mounted into the full-screen layer and back.
 */
export interface InkPage {
  strokes: Stroke[];
  past: Stroke[][];
  future: Stroke[][];
  /** Ids whose ink has been resolved into notation and is no longer painted. */
  hidden: string[];
  selected: string[];
}

export function emptyInkPage(): InkPage {
  return { strokes: [], past: [], future: [], hidden: [], selected: [] };
}

export interface InkEngineOptions {
  onStrokesChanged?: (strokes: Stroke[]) => void;
  /** Called once per completed stroke, for live/auto recognition. */
  onStrokeEnd?: (stroke: Stroke) => void;
  /** Called whenever the lasso selection changes, with the selected stroke ids. */
  onSelectionChanged?: (ids: string[]) => void;
  /**
   * Called once per completed selection edit — a move, scale, duplicate or
   * delete. Distinct from `onStrokeEnd`, which only fires for drawing, and from
   * `onStrokesChanged`, which also fires for undo and for every erase sample.
   */
  onEdit?: () => void;
  /**
   * The pointer went down and a gesture is now in progress.
   *
   * The pad uses this to stop the world: while it is true, no resize, no staff
   * reflow and no navigation may happen. Safari collapses its toolbars *while*
   * you write, which fires a ResizeObserver, which rebuilt the canvas backing
   * store and re-hydrated the page from staff-relative units — under the pen.
   * That is the "screen moves and glitches while writing" report.
   */
  onGestureStart?: () => void;
  /** The gesture ended, by any route: up, cancel, lost capture or teardown. */
  onGestureEnd?: () => void;
}

/**
 * Palm rejection window. Rather than one fixed timeout, the grace period adapts to
 * how you are actually writing: someone mid-sentence lifts and re-plants the pen
 * every few hundred milliseconds and needs a long window, while someone who wrote
 * once and stopped should get their finger back quickly.
 */
const PALM_GRACE_MIN_MS = 700;
const PALM_GRACE_MAX_MS = 2600;
/** How many recent pen-down gaps to average when sizing the window. */
const CADENCE_SAMPLES = 5;

/**
 * A touch contact wider than this many CSS pixels is a palm, not a fingertip.
 * Safari reports contact geometry via `width`/`height` on touch pointer events; a
 * fingertip is roughly 20-40px, the side of a hand is far larger.
 */
const PALM_CONTACT_PX = 45;

/**
 * A lasso smaller than this many square pixels was a tap, not a loop. Tapping
 * with the lasso tool clears the selection instead of selecting nothing, which
 * is what every drawing app does and what the hand expects.
 */
const MIN_LASSO_AREA = 240;

/** How far a duplicate lands from its original, so the copy is visibly separate. */
const DUPLICATE_OFFSET: Point2D = { x: 26, y: 14 };

/** Grab radius around the selection box for starting a drag. */
const DRAG_GRAB_PADDING = 12;

/** Cap on undo depth. Snapshots are arrays of shared references, so this is cheap. */
const MAX_HISTORY = 120;

let idCounter = 0;
const nextId = () => `s${Date.now().toString(36)}${(idCounter++).toString(36)}`;

/** What a lasso-tool drag is currently doing. */
type LassoMode = 'idle' | 'drawing' | 'moving';

/**
 * Should Pencil-only mode start switched on? On a touch device with a coarse
 * primary pointer — an iPad — yes: the whole point is to rest your hand while you
 * write. On a laptop it would leave the user unable to draw at all.
 */
export function prefersPencilOnly(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const coarse = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
  return coarse && (navigator.maxTouchPoints ?? 0) > 1;
}

export class InkEngine {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private strokes: Stroke[] = [];
  /**
   * Undo history as snapshots of the stroke list rather than a stack of strokes.
   * Drawing is not the only thing that changes the page any more — moving,
   * scaling, duplicating and erasing all have to be undoable, and none of them
   * is expressible as "pop the last stroke".
   */
  private past: Stroke[][] = [];
  private future: Stroke[][] = [];
  private live: Stroke | null = null;
  private activePointerId: number | null = null;
  private activePointerType: string | null = null;
  private lastPenAt = 0;
  /** Gaps between recent pen-downs, used to size the palm grace window. */
  private penCadence: number[] = [];
  private lastPenDownAt = 0;
  private startedAt = performance.now();
  private dpr = 1;
  private options: InkEngineOptions;
  /** Strokes that have been resolved into notation and are no longer painted. */
  private hidden = new Set<string>();
  /** Per-stroke opacity, used while a resolved stroke fades out. */
  private alpha = new Map<string, number>();
  /**
   * Every animation frame currently running a fade. Recognition fires every
   * 160ms and a fade lasts 260ms, so two can overlap; tracking only the newest
   * handle would leave an orphan loop free to re-hide ink after a cancel.
   */
  private fadeFrames = new Set<number>();

  // ------------------------------------------------------------- selection
  /** Ids caught by the lasso. Order does not matter; membership does. */
  private selected = new Set<string>();
  /** The loop currently being drawn, in canvas pixels. */
  private lassoPath: Point2D[] = [];
  private lassoMode: LassoMode = 'idle';
  private dragFrom: Point2D | null = null;
  /** Live drag offset, applied at paint time so a move is one history entry. */
  private dragOffset: Point2D = { x: 0, y: 0 };
  /** True once the current erase gesture has taken its undo snapshot. */
  private eraseSnapshotTaken = false;

  tool: InkTool = 'pen';
  color = '#12161d';
  width = 7.5;
  /** When false, finger and mouse can draw too (useful on a laptop). */
  penOnly = false;
  /**
   * Staff geometry, handed in by the pad. Selection needs it to quantise vertical
   * movement to staff steps; without it a drag is simply free-form.
   */
  layout: StaffLayout | null = null;
  /** Quantise vertical drags to whole staff steps. */
  snapToStaff = true;

  constructor(canvas: HTMLCanvasElement, options: InkEngineOptions = {}) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { desynchronized: true, alpha: true });
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.ctx = ctx;
    this.options = options;

    canvas.style.touchAction = 'none';
    canvas.addEventListener('pointerdown', this.handleDown);
    canvas.addEventListener('pointermove', this.handleMove);
    canvas.addEventListener('pointerup', this.handleUp);
    canvas.addEventListener('pointercancel', this.handleCancel);
    canvas.addEventListener('pointerleave', this.handleUp);
    canvas.addEventListener('lostpointercapture', this.handleLostCapture);
    // Safari still fires a synthetic double-tap zoom without this.
    canvas.addEventListener('touchstart', this.preventDefault, { passive: false });
    canvas.addEventListener('touchmove', this.preventDefault, { passive: false });
  }

  /**
   * Is a pointer gesture in progress right now?
   *
   * True from `pointerdown` until the stroke is closed out, whichever way that
   * happens. Anything that would move, resize or re-lay-out the manuscript has
   * to wait for this to go false.
   */
  isDrawing(): boolean {
    return this.activePointerId !== null;
  }

  destroy() {
    // A teardown mid-stroke still ends the gesture, or whoever is waiting on
    // `onGestureEnd` to flush a queued resize waits forever.
    if (this.activePointerId !== null) {
      this.activePointerId = null;
      this.activePointerType = null;
      this.options.onGestureEnd?.();
    }
    // An in-flight fade would otherwise keep repainting a detached canvas.
    this.cancelFades();
    const c = this.canvas;
    c.removeEventListener('pointerdown', this.handleDown);
    c.removeEventListener('pointermove', this.handleMove);
    c.removeEventListener('pointerup', this.handleUp);
    c.removeEventListener('pointercancel', this.handleCancel);
    c.removeEventListener('pointerleave', this.handleUp);
    c.removeEventListener('lostpointercapture', this.handleLostCapture);
    c.removeEventListener('touchstart', this.preventDefault);
    c.removeEventListener('touchmove', this.preventDefault);
  }

  private preventDefault = (e: Event) => {
    e.preventDefault();
  };

  /** Match backing-store size to CSS size × devicePixelRatio. */
  resize(cssWidth: number, cssHeight: number) {
    this.dpr = Math.min(window.devicePixelRatio || 1, 3);
    this.canvas.width = Math.max(1, Math.round(cssWidth * this.dpr));
    this.canvas.height = Math.max(1, Math.round(cssHeight * this.dpr));
    this.canvas.style.width = `${cssWidth}px`;
    this.canvas.style.height = `${cssHeight}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.repaint();
  }

  getStrokes(): Stroke[] {
    return this.strokes;
  }

  setStrokes(strokes: Stroke[]) {
    this.pushHistory();
    this.strokes = strokes;
    this.setSelection([]);
    this.repaint();
    this.options.onStrokesChanged?.(this.strokes);
  }

  clear() {
    if (!this.strokes.length) return;
    this.pushHistory();
    this.strokes = [];
    this.setSelection([]);
    this.resetVisibility();
    this.repaint();
    this.options.onStrokesChanged?.(this.strokes);
  }

  /** Snapshot the current page before mutating it, and drop the redo branch. */
  private pushHistory() {
    this.past.push(this.strokes.slice());
    if (this.past.length > MAX_HISTORY) this.past.shift();
    this.future = [];
  }

  // ------------------------------------------------------- resolved-ink fading

  /**
   * Fade the given strokes out and stop painting them. Called once handwriting
   * has been recognised, so the ink appears to *become* the engraved note that
   * animates into place above it. The strokes themselves are untouched, so undo
   * and `showAllInk()` both still work.
   */
  fadeOut(ids: string[], duration = 260) {
    // Nothing fades while the lasso is out. Editing writing you cannot see is
    // the one thing this animation must never cause.
    if (this.tool === 'lasso') return;
    // Selected ink stays visible for the same reason, whatever tool is active.
    const pending = ids.filter(
      (id) => !this.hidden.has(id) && !this.alpha.has(id) && !this.selected.has(id),
    );
    if (!pending.length) return;
    for (const id of pending) this.alpha.set(id, 1);

    const start = performance.now();
    let frame = 0;
    const step = () => {
      this.fadeFrames.delete(frame);
      // A cancel between frames drops this run's ids from `alpha`; if they are
      // gone, the fade was called off and must not resume hiding them.
      if (!pending.some((id) => this.alpha.has(id))) return;
      const progress = Math.min(1, (performance.now() - start) / duration);
      for (const id of pending) this.alpha.set(id, 1 - progress);
      this.repaint();
      if (progress < 1) {
        frame = requestAnimationFrame(step);
        this.fadeFrames.add(frame);
      } else {
        for (const id of pending) {
          this.alpha.delete(id);
          this.hidden.add(id);
        }
        this.repaint();
      }
    };
    frame = requestAnimationFrame(step);
    this.fadeFrames.add(frame);
  }

  /** True while ink is mid-fade — it counts as hidden for the "Show ink" button. */
  isFading(): boolean {
    return this.alpha.size > 0;
  }

  private cancelFades() {
    for (const frame of this.fadeFrames) cancelAnimationFrame(frame);
    this.fadeFrames.clear();
  }

  /** Bring every faded stroke back — the "show my handwriting" escape hatch. */
  showAllInk() {
    if (!this.hidden.size && !this.alpha.size) return;
    this.resetVisibility();
    this.repaint();
  }

  hasHiddenInk(): boolean {
    return this.hidden.size > 0;
  }

  private resetVisibility() {
    this.cancelFades();
    this.hidden.clear();
    this.alpha.clear();
  }

  undo() {
    const previous = this.past.pop();
    if (!previous) return;
    this.future.push(this.strokes.slice());
    this.strokes = previous;
    // Undo should give back what you drew, including anything that had already
    // been resolved into notation.
    this.resetVisibility();
    this.pruneSelection();
    this.repaint();
    this.options.onStrokesChanged?.(this.strokes);
  }

  redo() {
    const next = this.future.pop();
    if (!next) return;
    this.past.push(this.strokes.slice());
    this.strokes = next;
    this.pruneSelection();
    this.repaint();
    this.options.onStrokesChanged?.(this.strokes);
  }

  canUndo() {
    return this.past.length > 0;
  }

  canRedo() {
    return this.future.length > 0;
  }

  // ------------------------------------------------------------ page transfer

  /**
   * The whole page, in a form that survives this engine.
   *
   * The engine is bound to one canvas, and a canvas cannot move between React
   * parents — entering full screen therefore destroys it. Everything the learner
   * would notice is in here, so a new engine on a new canvas can pick the page up
   * exactly where the old one put it down. Undo history is part of that: losing
   * it is quieter than losing strokes and just as much a lost session.
   *
   * Snapshots are arrays of shared stroke references, so this is cheap enough to
   * run on every unmount.
   */
  serialize(): InkPage {
    return {
      strokes: this.strokes.slice(),
      past: this.past.map((page) => page.slice()),
      future: this.future.map((page) => page.slice()),
      hidden: [...this.hidden],
      selected: [...this.selected],
    };
  }

  /**
   * Adopt a page. Deliberately not `setStrokes`, which pushes an undo entry —
   * re-mounting is not an edit, and it must not be undoable.
   */
  hydrate(page: InkPage) {
    this.cancelFades();
    this.strokes = page.strokes.slice();
    this.past = page.past.map((entry) => entry.slice());
    this.future = page.future.map((entry) => entry.slice());
    this.hidden = new Set(page.hidden);
    this.alpha.clear();
    // Straight to the field: `setSelection` would announce a change the learner
    // did not make, and the pad reads the selection back on mount anyway.
    this.selected = new Set(page.selected);
    this.repaint();
    this.options.onStrokesChanged?.(this.strokes);
  }

  // --------------------------------------------------------------- selection

  getSelection(): string[] {
    return [...this.selected];
  }

  hasSelection(): boolean {
    return this.selected.size > 0;
  }

  /** Bounding box of the current selection, including any live drag offset. */
  getSelectionBounds() {
    const b = selectionBounds(this.strokes, this.selected);
    if (!b) return null;
    const { x, y } = this.dragOffset;
    return { minX: b.minX + x, minY: b.minY + y, maxX: b.maxX + x, maxY: b.maxY + y };
  }

  private setSelection(ids: string[]) {
    const changed =
      ids.length !== this.selected.size || ids.some((id) => !this.selected.has(id));
    this.selected = new Set(ids);
    if (changed) this.options.onSelectionChanged?.([...this.selected]);
  }

  /** Drop ids that no longer exist — after undo, erase, or a transform. */
  private pruneSelection() {
    if (!this.selected.size) return;
    const alive = new Set(this.strokes.map((s) => s.id));
    const kept = [...this.selected].filter((id) => alive.has(id));
    if (kept.length !== this.selected.size) this.setSelection(kept);
  }

  clearSelection() {
    if (!this.selected.size && !this.lassoPath.length && this.lassoMode === 'idle') return;
    this.lassoPath = [];
    // Deselecting mid-drag abandons the drag. Leaving `lassoMode` at 'moving'
    // would let the release commit a transform against an empty selection —
    // a history entry that changes nothing and swallows the redo branch.
    if (this.lassoMode === 'moving') {
      this.lassoMode = 'idle';
      this.dragFrom = null;
      this.dragOffset = { x: 0, y: 0 };
    }
    this.setSelection([]);
    this.repaint();
  }

  /**
   * Move the selection. `snap` quantises the vertical distance to whole staff
   * steps, so dragging a chord up lands it on lines and spaces rather than
   * between them.
   */
  moveSelection(dx: number, dy: number, snap = false) {
    if (!this.selected.size) return;
    const appliedY = snap && this.layout ? snapDeltaY(dy, this.layout) : dy;
    if (dx === 0 && appliedY === 0) return;
    this.pushHistory();
    this.strokes = transformSelection(this.strokes, this.selected, { dx, dy: appliedY });
    this.afterEdit();
  }

  /** Scale the selection about its own centre. */
  scaleSelection(factor: number) {
    const bounds = selectionBounds(this.strokes, this.selected);
    if (!bounds || factor === 1) return;
    const origin = {
      x: (bounds.minX + bounds.maxX) / 2,
      y: (bounds.minY + bounds.maxY) / 2,
    };
    this.pushHistory();
    this.strokes = transformSelection(this.strokes, this.selected, { scale: factor, origin });
    this.afterEdit();
  }

  deleteSelection() {
    if (!this.selected.size) return;
    this.pushHistory();
    this.strokes = deleteSelection(this.strokes, this.selected);
    this.setSelection([]);
    this.afterEdit();
  }

  /** Copy the selection and select the copy, so the next drag moves the new one. */
  duplicateSelection(offset: Point2D = DUPLICATE_OFFSET) {
    if (!this.selected.size) return;
    this.pushHistory();
    const result = duplicateSelection(this.strokes, this.selected, offset, nextId);
    this.strokes = result.strokes;
    this.setSelection(result.ids);
    this.afterEdit();
  }

  /**
   * Shared tail of every selection edit. Faded ink is brought back because you
   * cannot reasonably edit writing you cannot see, and the pad re-reads the page
   * afterwards.
   */
  private afterEdit() {
    this.resetVisibility();
    this.pruneSelection();
    this.repaint();
    this.options.onStrokesChanged?.(this.strokes);
    // The ink has moved, so the reading of it is now stale. Say so — otherwise
    // the engraved note and the pitch label sit over where the note used to be.
    this.options.onEdit?.();
  }

  // ---------------------------------------------------------------- pointers

  /**
   * How long to keep ignoring touch after the last pen sample. Writing in quick
   * strokes widens the window; a single stroke followed by a pause narrows it, so
   * the finger becomes usable again without waiting out a worst-case timeout.
   */
  palmGraceMs(): number {
    if (!this.penCadence.length) return PALM_GRACE_MIN_MS;
    const mean = this.penCadence.reduce((a, b) => a + b, 0) / this.penCadence.length;
    // Leave room for roughly two more strokes at the current pace.
    return Math.max(PALM_GRACE_MIN_MS, Math.min(PALM_GRACE_MAX_MS, mean * 2));
  }

  /** True when a touch contact is too broad to be a fingertip. */
  private isPalmContact(e: PointerEvent): boolean {
    if (e.pointerType !== 'touch') return false;
    // width/height are 1 when the platform does not report contact geometry.
    const w = e.width ?? 0;
    const h = e.height ?? 0;
    if (w <= 1 && h <= 1) return false;
    return Math.max(w, h) >= PALM_CONTACT_PX;
  }

  private shouldAccept(e: PointerEvent): boolean {
    if (e.pointerType === 'pen') return true;
    if (e.pointerType === 'touch') {
      // A broad contact is a resting hand whether or not the pen is near the glass.
      if (this.isPalmContact(e)) return false;
      // A finger landing while the pen is in use is part of the hand holding it.
      // Zero means "no pen sample yet", not "the pen was seen at navigation
      // start". Without the guard a freshly opened staff rejected fingers for
      // the first grace window solely because performance.now() was still low.
      if (this.lastPenAt > 0 && performance.now() - this.lastPenAt < this.palmGraceMs()) {
        return false;
      }
      return !this.penOnly;
    }
    return !this.penOnly; // mouse / trackpad
  }

  /** A pen hovering above the glass reports no buttons and no pressure. */
  private isHover(e: PointerEvent): boolean {
    return e.pointerType === 'pen' && e.buttons === 0 && e.pressure === 0;
  }

  private toPoint(e: PointerEvent): InkPoint {
    const rect = this.canvas.getBoundingClientRect();
    const isPen = e.pointerType === 'pen';
    // Safari reports 0 pressure for mouse; give non-pen input a stable mid value
    // so lines do not randomly hairline out.
    const pressure = isPen ? (e.pressure > 0 ? e.pressure : 0.35) : 0.5;
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      pressure,
      tiltX: e.tiltX ?? 0,
      tiltY: e.tiltY ?? 0,
      t: performance.now() - this.startedAt,
    };
  }

  private handleDown = (e: PointerEvent) => {
    // `touch-action: none` is the primary contract, but iPad Safari has shipped
    // Pencil builds that still begin viewport handling before pointer capture.
    // Cancelling the accepted pointer event closes that escape hatch.
    if (e.cancelable) e.preventDefault();
    const now = performance.now();
    if (e.pointerType === 'pen') {
      this.lastPenAt = now;
      if (this.lastPenDownAt > 0) {
        this.penCadence.push(now - this.lastPenDownAt);
        if (this.penCadence.length > CADENCE_SAMPLES) this.penCadence.shift();
      }
      this.lastPenDownAt = now;
    }
    if (this.isHover(e)) return; // hovering is not writing
    if (!this.shouldAccept(e)) return;

    if (this.activePointerId !== null) {
      // The pen taking over from a finger, or landing again before the previous
      // stroke was closed out, must not be dropped — that shows up as a missing
      // mark when writing quickly.
      const penInterrupting = e.pointerType === 'pen' && this.activePointerType !== 'pen';
      if (!penInterrupting && e.pointerType !== 'pen') return;
      this.finishStroke();
    }

    try {
      this.canvas.setPointerCapture(e.pointerId);
    } catch {
      // Capture can fail if the pointer already went away; drawing still works.
    }
    const wasIdle = this.activePointerId === null;
    this.activePointerId = e.pointerId;
    this.activePointerType = e.pointerType;
    if (wasIdle) this.options.onGestureStart?.();

    const point = this.toPoint(e);

    if (this.tool === 'eraser') {
      this.eraseSnapshotTaken = false;
      this.eraseAt(point.x, point.y);
      return;
    }

    if (this.tool === 'lasso') {
      // Landing inside an existing selection grabs it; landing anywhere else
      // starts a new loop. This is the gesture people already know.
      const bounds = this.getSelectionBounds();
      const insideSelection =
        bounds !== null &&
        point.x >= bounds.minX - DRAG_GRAB_PADDING &&
        point.x <= bounds.maxX + DRAG_GRAB_PADDING &&
        point.y >= bounds.minY - DRAG_GRAB_PADDING &&
        point.y <= bounds.maxY + DRAG_GRAB_PADDING;

      if (insideSelection) {
        this.lassoMode = 'moving';
        this.dragFrom = { x: point.x, y: point.y };
        this.dragOffset = { x: 0, y: 0 };
      } else {
        this.lassoMode = 'drawing';
        this.lassoPath = [{ x: point.x, y: point.y }];
      }
      return;
    }

    this.live = {
      id: nextId(),
      points: [point],
      color: this.tool === 'highlighter' ? 'rgba(212,167,79,0.34)' : this.color,
      width: this.tool === 'highlighter' ? this.width * 4.5 : this.width,
      tool: this.tool,
      fromPen: e.pointerType === 'pen',
    };
  };

  private handleMove = (e: PointerEvent) => {
    // Track the pen even while it only hovers: the hand is already on the glass,
    // so palm rejection should be armed before the tip makes contact.
    if (e.pointerType === 'pen') this.lastPenAt = performance.now();
    if (e.pointerId !== this.activePointerId) return;
    if (e.cancelable) e.preventDefault();

    if (this.tool === 'eraser') {
      const p = this.toPoint(e);
      this.eraseAt(p.x, p.y);
      return;
    }

    if (this.tool === 'lasso') {
      const p = this.toPoint(e);
      if (this.lassoMode === 'drawing') {
        const prev = this.lassoPath[this.lassoPath.length - 1];
        if (!prev || Math.hypot(p.x - prev.x, p.y - prev.y) >= 1.5) {
          this.lassoPath.push({ x: p.x, y: p.y });
          this.repaint();
        }
      } else if (this.lassoMode === 'moving' && this.dragFrom) {
        const dy = p.y - this.dragFrom.y;
        this.dragOffset = {
          x: p.x - this.dragFrom.x,
          // Quantise as the hand moves, not on release, so the ink is seen
          // stepping from line to space rather than jumping at the end.
          y: this.snapToStaff && this.layout ? snapDeltaY(dy, this.layout) : dy,
        };
        this.repaint();
      }
      return;
    }

    if (!this.live) return;

    // Coalesced events give us every sample the digitiser captured between frames.
    const events: PointerEvent[] =
      typeof e.getCoalescedEvents === 'function' && e.getCoalescedEvents().length
        ? (e.getCoalescedEvents() as PointerEvent[])
        : [e];

    for (const ev of events) {
      const point = this.toPoint(ev);
      const prev = this.live.points[this.live.points.length - 1];
      // Drop samples that add nothing; keeps stroke arrays small for recognition.
      if (Math.hypot(point.x - prev.x, point.y - prev.y) < 0.4) continue;
      this.live.points.push(point);
    }
    this.drawLiveTail();
  };

  private handleUp = (e: PointerEvent) => {
    if (e.pointerId !== this.activePointerId) return;
    if (e.cancelable) e.preventDefault();
    try {
      if (this.canvas.hasPointerCapture(e.pointerId)) this.canvas.releasePointerCapture(e.pointerId);
    } catch {
      // Already released, or the pointer is gone. Nothing to do.
    }
    this.finishStroke();
  };

  /**
   * Capture can be taken away mid-stroke — a system gesture, a second contact,
   * Safari deciding to scroll. Close the stroke out cleanly rather than leaving a
   * live stroke that later collects phantom points.
   */
  private handleLostCapture = (e: PointerEvent) => {
    if (e.pointerId !== this.activePointerId) return;
    this.finishStroke();
  };

  /**
   * `pointercancel` means the gesture did not happen — iPadOS took the pointer
   * for a system swipe, or a palm landed. Committing a half-finished *stroke* is
   * harmless, but committing a half-finished *move* would leave the learner's
   * writing wherever their hand happened to be when the swipe started. Abandon
   * the drag instead and put the ink back where it was.
   */
  private handleCancel = (e: PointerEvent) => {
    if (e.pointerId !== this.activePointerId) return;
    if (e.cancelable) e.preventDefault();
    if (this.lassoMode === 'moving') {
      this.lassoMode = 'idle';
      this.dragFrom = null;
      this.dragOffset = { x: 0, y: 0 };
      this.activePointerId = null;
      this.activePointerType = null;
      // This branch returns without reaching `finishStroke`, so it has to end
      // the gesture itself — otherwise a cancelled drag leaves the pad believing
      // a stroke is still live and every layout update queued behind it.
      this.options.onGestureEnd?.();
      this.repaint();
      return;
    }
    this.finishStroke();
  };

  /** Commit whatever is being drawn and clear the active pointer. */
  private finishStroke() {
    const wasActive = this.activePointerId !== null;
    this.activePointerId = null;
    this.activePointerType = null;
    if (wasActive) this.options.onGestureEnd?.();

    if (this.lassoMode !== 'idle') {
      this.finishLasso();
      return;
    }

    const finished = this.live;
    this.live = null;
    if (!finished) return;

    // A tap with no movement is still a dot — give it a second point so it renders.
    if (finished.points.length === 1) {
      finished.points.push({ ...finished.points[0], x: finished.points[0].x + 0.01 });
    }
    this.pushHistory();
    this.strokes.push(finished);
    this.repaint();
    this.options.onStrokesChanged?.(this.strokes);
    this.options.onStrokeEnd?.(finished);
  }

  /** Close out a lasso gesture: either a new selection, or a committed move. */
  private finishLasso() {
    const mode = this.lassoMode;
    this.lassoMode = 'idle';

    if (mode === 'drawing') {
      const path = this.lassoPath;
      this.lassoPath = [];
      // A tap rather than a loop means "deselect", which is what the hand
      // expects and what saves a trip to a Deselect button.
      if (path.length < 3 || polygonArea(path) < MIN_LASSO_AREA) {
        this.setSelection([]);
        this.repaint();
        return;
      }
      // Faded ink is still on the page and still selectable; bring it back so
      // the learner can see what they just caught.
      const caught = strokesInLasso(this.strokes, path);
      if (caught.length) this.resetVisibility();
      this.setSelection(caught);
      this.repaint();
      return;
    }

    // mode === 'moving': fold the previewed offset into the strokes as one edit.
    const { x, y } = this.dragOffset;
    this.dragFrom = null;
    this.dragOffset = { x: 0, y: 0 };
    if ((x === 0 && y === 0) || !this.selected.size) {
      this.repaint();
      return;
    }
    this.pushHistory();
    this.strokes = transformSelection(this.strokes, this.selected, { dx: x, dy: y });
    this.afterEdit();
  }

  private eraseAt(x: number, y: number) {
    const radius = 14;
    const before = this.strokes.length;
    const survivors = this.strokes.filter((stroke) => {
      const b = strokeBounds(stroke);
      if (x < b.minX - radius || x > b.maxX + radius || y < b.minY - radius || y > b.maxY + radius) {
        return true;
      }
      return !stroke.points.some((p) => Math.hypot(p.x - x, p.y - y) <= radius);
    });
    if (survivors.length !== before) {
      // One snapshot per erase gesture, not per pointer sample — otherwise a
      // single wipe costs a dozen taps of Undo to reverse.
      if (!this.eraseSnapshotTaken) {
        this.pushHistory();
        this.eraseSnapshotTaken = true;
      }
      this.strokes = survivors;
      this.pruneSelection();
      this.repaint();
      this.options.onStrokesChanged?.(this.strokes);
    }
  }

  // ---------------------------------------------------------------- painting

  /** Width at a point: pressure-driven, with velocity thinning for a pen-like taper. */
  private widthAt(stroke: Stroke, index: number): number {
    const p = stroke.points[index];
    const base = stroke.width;
    if (stroke.tool === 'highlighter') return base;
    // Keep a light Pencil stroke visible. The old curve could shrink a 6px pen
    // below 3px at normal light pressure, so nominal width alone was misleading.
    const pressureFactor = 0.68 + p.pressure * 0.55;
    let velocityFactor = 1;
    if (index > 0) {
      const prev = stroke.points[index - 1];
      const dt = Math.max(1, p.t - prev.t);
      const speed = Math.hypot(p.x - prev.x, p.y - prev.y) / dt; // px per ms
      velocityFactor = 1 / (1 + speed * 0.28);
      velocityFactor = 0.82 + velocityFactor * 0.18;
    }
    return Math.max(0.6, base * pressureFactor * velocityFactor);
  }

  private paintStroke(stroke: Stroke, from = 0) {
    const ctx = this.ctx;
    const pts = stroke.points;
    if (pts.length < 2) return;
    if (this.hidden.has(stroke.id)) return;

    ctx.save();
    const opacity = this.alpha.get(stroke.id);
    if (opacity !== undefined) ctx.globalAlpha = opacity;
    // A drag is previewed by translating the selected ink at paint time, so the
    // stroke data is written once on release instead of on every frame.
    if (this.selected.has(stroke.id) && (this.dragOffset.x || this.dragOffset.y)) {
      ctx.translate(this.dragOffset.x, this.dragOffset.y);
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = stroke.color;
    if (stroke.tool === 'highlighter') {
      ctx.globalCompositeOperation = 'multiply';
      ctx.lineCap = 'butt';
    }

    const start = Math.max(1, from);
    for (let i = start; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      // Quadratic through the midpoints keeps the curve smooth without a full spline pass.
      const midPrev = i > 1 ? { x: (pts[i - 2].x + a.x) / 2, y: (pts[i - 2].y + a.y) / 2 } : a;
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      ctx.lineWidth = this.widthAt(stroke, i);
      ctx.beginPath();
      ctx.moveTo(midPrev.x, midPrev.y);
      ctx.quadraticCurveTo(a.x, a.y, mid.x, mid.y);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** Draw only the newest segment of the live stroke — the low-latency path. */
  private drawLiveTail() {
    if (!this.live) return;
    this.paintStroke(this.live, Math.max(1, this.live.points.length - 3));
  }

  /** The loop being drawn, and the box around whatever it has caught. */
  private paintSelectionOverlay() {
    const ctx = this.ctx;

    if (this.lassoPath.length > 1) {
      ctx.save();
      ctx.setLineDash([6, 5]);
      ctx.lineWidth = 1.4;
      ctx.strokeStyle = 'rgba(212, 167, 79, 0.95)';
      ctx.fillStyle = 'rgba(212, 167, 79, 0.10)';
      ctx.beginPath();
      ctx.moveTo(this.lassoPath[0].x, this.lassoPath[0].y);
      for (const p of this.lassoPath.slice(1)) ctx.lineTo(p.x, p.y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      return;
    }

    const bounds = this.getSelectionBounds();
    if (!bounds) return;
    const pad = 6;
    ctx.save();
    ctx.setLineDash([5, 4]);
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = 'rgba(212, 167, 79, 0.9)';
    ctx.fillStyle = 'rgba(212, 167, 79, 0.08)';
    const x = bounds.minX - pad;
    const y = bounds.minY - pad;
    const w = bounds.maxX - bounds.minX + pad * 2;
    const h = bounds.maxY - bounds.minY + pad * 2;
    ctx.fillRect(x, y, w, h);
    ctx.strokeRect(x, y, w, h);
    ctx.restore();
  }

  repaint() {
    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.restore();
    // The selection box sits under the ink so it never obscures handwriting.
    this.paintSelectionOverlay();
    for (const stroke of this.strokes) this.paintStroke(stroke);
    if (this.live) this.paintStroke(this.live);
  }
}
